import { api } from './api';
import type { AxiosError } from 'axios';
import { createEnhancedError } from '../utils/error-handling';

// Enum types matching backend

export enum GewType {
  NEUBAU = 'wohnung-neubau',
  BESTAND = 'wohnung-bestand',
  GRUNDSTUECK = 'grundstueck',
  HAUS_NEUBAU = 'haus-neubau',
  HAUS_BESTAND = 'haus-bestand',
}

export enum PropertyQuality {
  MAESSIG = 'mäßig',
  GUT = 'gut',
  SEHR_GUT = 'sehr gut',
}

// Display labels for GewType / Immobilientyp
export const GewTypeLabels: Record<string, string> = {
  [GewType.GRUNDSTUECK]: 'Grundstück',
  [GewType.HAUS_NEUBAU]: 'Haus (Neubau)',
  [GewType.HAUS_BESTAND]: 'Haus (Bestand)',
  [GewType.NEUBAU]: 'Wohnung (Neubau)',
  [GewType.BESTAND]: 'Wohnung (Bestand)',
};

export const formatGewType = (gewType?: GewType | string | null): string => {
  if (!gewType) return '-';
  return GewTypeLabels[gewType] || String(gewType);
};

/**
 * Formats a number using German locale ('de-DE'):
 * - ',' as decimal separator
 * - '.' as thousand separator
 */
export const formatNumber = (
  value: number | string | undefined | null,
  options?: Intl.NumberFormatOptions,
): string => {
  if (value === undefined || value === null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return num.toLocaleString('de-DE', options);
};

/**
 * Formats a currency value in EUR with '€' at the end:
 * e.g. 199.999,99 € or 199.999 €
 */
export const formatCurrency = (
  value: number | string | undefined | null,
  options?: { showDecimalsIfZero?: boolean; maxDecimals?: number },
): string => {
  if (value === undefined || value === null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  const hasDecimals = num % 1 !== 0;
  const numDigits =
    options?.showDecimalsIfZero || hasDecimals
      ? (options?.maxDecimals ?? 2)
      : 0;
  const formatted = num.toLocaleString('de-DE', {
    minimumFractionDigits: numDigits,
    maximumFractionDigits: options?.maxDecimals ?? 2,
  });
  return `${formatted} €`;
};

/**
 * Formats a percentage value with German formatting:
 * e.g. 3,5 % or 12,50 %
 */
export const formatPercent = (
  value: number | string | undefined | null,
  decimals: number = 1,
): string => {
  if (value === undefined || value === null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return `${num.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} %`;
};

// Display labels for Quality
export const PropertyQualityLabels: Record<PropertyQuality, string> = {
  [PropertyQuality.MAESSIG]: 'Mäßig',
  [PropertyQuality.GUT]: 'Gut',
  [PropertyQuality.SEHR_GUT]: 'Sehr Gut',
};

export interface PropertyFeatures {
  [key: string]: any;
}

export interface PropertyRatings {
  [key: string]: any;
}

// Property data interface matching backend DTO
export interface PropertyData {
  id: string;
  // Common fields
  address: string;
  streetName?: string;
  houseNumber?: string;
  city?: string;
  gewGemNr?: number;
  country?: string;
  latitude?: number;
  longitude?: number;
  gewType?: GewType;
  quality?: PropertyQuality;
  // ETW fields
  area?: number;
  plotArea?: number;
  baujahr?: number;

  // Financial data
  kaufdatum?: string;
  kaufpreis?: number;
  currentValue?: number;
  gain: number;
  gainPercentage: number;
  isFallbackEvaluation?: boolean;
  fremdfinanziert?: boolean;
  eigenkapital?: number;
  fremdkapital?: number;
  zinssatz?: number;
  monatlicheRate?: number;
  laufzeit?: number;
  features: PropertyFeatures;
  ratings: PropertyRatings;
  sellAmount?: number;
  sellDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PropertyFile {
  id: string;
  filename: string;
  s3Key: string;
  mimeType: string;
  size: number;
  propertyId: string;
  url: string; // presigned url for viewing/downloading
  createdAt: string;
  updatedAt: string;
}

// Create property DTO
export interface CreatePropertyDto {
  address: string;
  streetName?: string;
  houseNumber: string;
  city?: string;
  gewGemNr?: number;
  country?: string;
  latitude?: number;
  longitude?: number;
  gewType?: GewType;
  quality?: PropertyQuality;
  // ETW fields
  area?: number;
  plotArea?: number;
  baujahr?: number;
  features?: PropertyFeatures;
  ratings: PropertyRatings;
  // Financial data
  kaufdatum?: string;
  kaufpreis?: number;
  currentValue?: number;
  fremdfinanziert: boolean;
  eigenkapital?: number;
  fremdkapital?: number;
  zinssatz?: number;
  monatlicheRate?: number;
  laufzeit?: number;
}

// DTO for selling a property
export interface SellPropertyDto {
  sellAmount: number;
  sellDate: string;
}

// DTO for buying a property
export interface BuyPropertyDto {
  kaufpreis: number;
  kaufdatum: string;
}

// Enhanced error handling for property-specific errors
const handlePropertyError = (error: AxiosError): never => {
  throw createEnhancedError(error);
};

// Property API service
export const propertyApi = {
  // Get all properties for the authenticated user
  getAll: async (): Promise<PropertyData[]> => {
    try {
      const response = await api.get<PropertyData[]>('/properties');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch properties');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get a single property by ID
  getById: async (id: string): Promise<PropertyData> => {
    try {
      const response = await api.get<PropertyData>(`/properties/${id}`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Property not found');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Create a new property
  create: async (property: CreatePropertyDto): Promise<PropertyData> => {
    try {
      const response = await api.post<PropertyData>('/properties', property);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to create property');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Update/sell an existing property
  // Sell a property
  sell: async (
    id: string,
    property: SellPropertyDto,
  ): Promise<PropertyData> => {
    try {
      const response = await api.put<PropertyData>(
        `/properties/${id}/sell`,
        property,
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to sell property');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Buy a property
  buy: async (id: string, property: BuyPropertyDto): Promise<PropertyData> => {
    try {
      const response = await api.put<PropertyData>(
        `/properties/${id}/buy`,
        property,
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to buy property');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Delete a property
  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/properties/${id}`);
      // DELETE returns 204 No Content, so no data to process
    } catch (error) {
      handlePropertyError(error as AxiosError);
    }
  },

  // Get property evaluations
  getEvaluations: async (
    id: string,
    period?: '1month' | '6months' | '12months' | '5years' | 'all',
  ): Promise<PropertyEvaluation[]> => {
    try {
      const params = period ? { period } : {};
      const response = await api.get<PropertyEvaluation[]>(
        `/properties/${id}/evaluations`,
        { params },
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch property evaluations');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get property city distribution
  getCityDistribution: async (): Promise<
    { label: string; value: number }[]
  > => {
    try {
      const response = await api.get<{ label: string; value: number }[]>(
        '/properties/stats/city-distribution',
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch city distribution');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get condition classification config
  getConditionClassificationConfig: async (gewType?: string): Promise<any> => {
    try {
      const response = await api.get<any>(
        '/properties/condition-classification/config',
        {
          params: gewType ? { gewType } : {},
        },
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch condition classification config');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get property files
  getFiles: async (propertyId: string): Promise<PropertyFile[]> => {
    try {
      const response = await api.get<PropertyFile[]>(
        `/properties/${propertyId}/files`,
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Upload property file
  uploadFile: async (propertyId: string, file: File): Promise<PropertyFile> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      // We use apiClient directly here to override headers easily
      const response = await import('./api').then((m) =>
        m.default.post(`/properties/${propertyId}/files`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      );
      if (response.data.success && response.data.data) {
        return response.data.data as PropertyFile;
      }
      throw new Error('Failed to upload file');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Upload local market data XLSX file (Admin only)
  uploadLocalMarketData: async (
    file: File,
  ): Promise<{ success: boolean; message: string; data: any }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await import('./api').then((m) =>
        m.default.post(`/properties/local-market-data/import`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      );
      if (response.data.success) {
        return response.data;
      }
      throw new Error(
        response.data.message || 'Failed to upload local market data',
      );
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get local market data imports (Admin only)
  getLocalMarketDataImports: async (): Promise<LocalMarketDataImport[]> => {
    try {
      const response = await api.get<LocalMarketDataImport[]>(
        '/properties/local-market-data/imports',
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch local market data imports');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Delete property file
  deleteFile: async (propertyId: string, fileId: string): Promise<void> => {
    try {
      await api.delete(`/properties/${propertyId}/files/${fileId}`);
    } catch (error) {
      handlePropertyError(error as AxiosError);
    }
  },

  // Get property evaluation breakdown (Admin only)
  getEvaluationBreakdown: async (id: string): Promise<EvaluationBreakdown> => {
    try {
      const response = await api.get<EvaluationBreakdown>(
        `/properties/${id}/evaluation-breakdown`,
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch property evaluation breakdown');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },
};

// Property evaluation interface
export interface PropertyEvaluation {
  id: string;
  vw?: number;
  vwLwr?: number; // camelCase to match backend entity
  vwUpr?: number; // camelCase to match backend entity
  timestamp: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClassificationBreakdown {
  category: string;
  percentageScore: number;
  staticValueTotal: number;
  fraction: number;
}

export interface DualClassificationBreakdown {
  building: ClassificationBreakdown;
  land: ClassificationBreakdown;
  thresholds: { low: number; high: number };
}

export interface MarketDataEntry {
  gewType: string;
  gewGemName: string;
  gewMin?: number;
  gewMax?: number;
  gewValue?: number;
  gewNmin?: number;
  gewNmax?: number;
  gewMmin?: number;
  gewMmax?: number;
  gewOmin?: number;
  gewOmax?: number;
}

export interface CalculationStep {
  valueLwr: number;
  valueUpr: number;
  valuePerSqm: number;
  area: number;
  total: number;
}

export interface LocalMarketDataImport {
  id: string;
  checksum: string;
  gewYear?: number;
  gewDate?: number;
  importedAt: string;
}

export interface EvaluationBreakdown {
  classification: DualClassificationBreakdown;
  buildingMarketData?: MarketDataEntry;
  landMarketData?: MarketDataEntry;
  buildingCalculation?: CalculationStep;
  landCalculation?: CalculationStep;
  mainCalculation?: CalculationStep;
  staticValueTotal: number;
  staticValueDetails: { name: string; value: number }[];
  isFallbackEvaluation: boolean;
  finalValue: number;
}

// Places API service
export const placesApi = {
  getAutocomplete: async (input: string, country?: string): Promise<any[]> => {
    if (!input) return [];
    try {
      const response = await api.get('/places/autocomplete', {
        params: { input, country },
      });
      if (response.data.success && response.data.data) {
        return response.data.data as any[];
      }
      return [];
    } catch (error) {
      console.error('Error fetching autocomplete suggestions:', error);
      return [];
    }
  },

  getPlaceDetails: async (placeId: string): Promise<any> => {
    try {
      const response = await api.get('/places/place-details', {
        params: { placeId },
      });
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching place details:', error);
      return null;
    }
  },


  getLocationByCoordinates: async (
    lat: number,
    lng: number,
  ): Promise<{ gewGemNr: number | null; gewGemName: string | null; city: string | null } | null> => {
    try {
      const response = await api.get<{
        city: string | null;
        gewGemNr: number | null;
        gewGemName: string | null;
      }>('/places/city-by-coordinates', {
        params: { lat, lng },
      });
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching location by coordinates:', error);
      return null;
    }
  },

  getCityByCoordinates: async (
    lat: number,
    lng: number,
    expectedCity?: string,
  ): Promise<string | null> => {
    try {
      const response = await api.get<{ city: string | null }>(
        '/places/city-by-coordinates',
        {
          params: { lat, lng, expectedCity },
        },
      );
      if (response.data.success && response.data.data) {
        return response.data.data.city;
      }
      return null;
    } catch (error) {
      console.error('Error fetching city by coordinates:', error);
      return null;
    }
  },
};
